import { HttpException, UnauthorizedException } from '../http';
import { deleteStudy, uploadDicomFile } from '../clients/viewer';
import { describeError } from './describeError';
import { ensurePreamble } from './dicomIntake';
import { UploadStatus } from './uploadTypes';

export interface BatchCallbacks {
  onFileStarted: (index: number) => void;
  onFileProgress: (index: number, percent: number) => void;
  onFileSettled: (index: number, status: UploadStatus, error?: string) => void;
}

export type BatchOutcome =
  | { type: 'completed' }
  | { type: 'rejected'; index: number; message: string }
  | { type: 'failed'; index: number; message: string }
  | { type: 'cancelled' }
  | { type: 'unauthorized' };

export interface BatchOptions {
  force: boolean;
  signal: AbortSignal;
  /** Accumulates across a halt and a resume, so Stop can roll the whole run back. */
  studyUids: Set<string>;
  callbacks: BatchCallbacks;
}

export async function runUploadBatch(
  files: readonly File[],
  indices: readonly number[],
  { force, signal, studyUids, callbacks }: BatchOptions
): Promise<BatchOutcome> {
  for (const index of indices) {
    if (signal.aborted) {
      callbacks.onFileSettled(index, UploadStatus.Cancelled);
      return { type: 'cancelled' };
    }

    callbacks.onFileStarted(index);

    const file = files[index];

    try {
      const prepared = await ensurePreamble(file);

      const studyInstanceUID = await uploadDicomFile(prepared, {
        force,
        signal,
        onProgress: (loaded: number, total: number) =>
          callbacks.onFileProgress(
            index,
            Math.min(100, Math.round((loaded * 100) / (total || file.size || 1)))
          ),
      });

      studyUids.add(studyInstanceUID);
      callbacks.onFileSettled(index, UploadStatus.Success);
    } catch (error) {
      if (signal.aborted) {
        callbacks.onFileSettled(index, UploadStatus.Cancelled);
        return { type: 'cancelled' };
      }

      if (error instanceof UnauthorizedException) {
        callbacks.onFileSettled(index, UploadStatus.Failed, error.message);
        return { type: 'unauthorized' };
      }

      const message = error instanceof Error ? error.message : 'Upload failed';

      callbacks.onFileSettled(index, UploadStatus.Failed, message);

      const isContentRejection =
        error instanceof HttpException && error.status >= 400 && error.status < 500;

      return { type: isContentRejection ? 'rejected' : 'failed', index, message };
    }
  }

  return { type: 'completed' };
}

export async function rollbackStudies(studyUids: Iterable<string>): Promise<string[]> {
  const failed: string[] = [];

  for (const studyInstanceUid of studyUids) {
    try {
      await deleteStudy(studyInstanceUid);
    } catch (error) {
      failed.push(studyInstanceUid);
      console.error(
        `[sycai] rollback DELETE failed for ${studyInstanceUid} ${describeError(error)}`
      );
    }
  }

  return failed;
}
